/**
 * Source Registry Management Component
 * P0.1 - Unified governance for all evidence sources
 * P1.1 Cleanup - Admin tools hidden behind dropdown
 * P1.4.1 Fix - Clickable drilldowns, working refresh, health badges
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Database, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Globe,
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Settings,
  Trash2,
  Plus,
  MoreVertical,
  Wrench,
  ExternalLink,
  Layers,
  AlertCircle,
  XCircle,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import SourceEvidenceWizard from './SourceEvidenceWizard';
import ScopedResetDialog from './ScopedResetDialog';
import { adminFetchHeaders } from '@/hooks/useAdminFetch';

// P0 FIX: Use same-origin API calls to avoid CORS issues in production
const API = ''; // Relative URL - browser uses same origin

const getAuthHeaders = () => adminFetchHeaders(true);

// Tier badge colors
const TIER_COLORS = {
  PRIMARY: 'bg-green-100 text-green-800 border-green-300',
  SECONDARY: 'bg-blue-100 text-blue-800 border-blue-300',
  TERTIARY: 'bg-gray-100 text-gray-800 border-gray-300'
};

// Status badge colors
const STATUS_COLORS = {
  ACTIVE: 'bg-green-100 text-green-800',
  PENDING_REVIEW: 'bg-yellow-100 text-yellow-800',
  DEPRECATED: 'bg-red-100 text-red-800',
  ARCHIVED: 'bg-gray-100 text-gray-600'
};

// Health status colors
const HEALTH_COLORS = {
  OK: 'bg-green-100 text-green-700 border-green-300',
  NO_EVIDENCE: 'bg-red-100 text-red-700 border-red-300',
  NO_CHUNKS: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  CHUNKING_FAILED: 'bg-orange-100 text-orange-700 border-orange-300'
};

const HEALTH_ICONS = {
  OK: CheckCircle,
  NO_EVIDENCE: XCircle,
  NO_CHUNKS: AlertCircle,
  CHUNKING_FAILED: AlertTriangle
};

// Source type icons
const TYPE_ICONS = {
  CONTROLLED_DOCUMENT: FileText,
  WEB_PDF_SNAPSHOT: Globe,
  WEB_CRAWL_SNAPSHOT: Globe
};

const SourceRegistryManager = ({ isActive = false, onNavigateToEvidence, onNavigateToChunks }) => {
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [expandedSource, setExpandedSource] = useState(null);
  const [tierModal, setTierModal] = useState({ open: false, source: null });
  const [showWizard, setShowWizard] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  
  // P1.4.1: Health data per source
  const [healthData, setHealthData] = useState({});
  const [loadingHealth, setLoadingHealth] = useState({});
  
  // P1.4.1: Filter toggle (Show All vs Only Effective)
  const [showOnlyEffective, setShowOnlyEffective] = useState(false);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/source-registry/stats/overview`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        throw new Error('Failed to fetch stats');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Fehler beim Laden der Statistiken');
    }
  }, []);

  // Fetch sources
  const fetchSources = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/source-registry/list`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // API returns { items: [...] }, extract the items array
        const sourcesList = data.items || data || [];
        setSources(sourcesList);
        return sourcesList;
      } else {
        throw new Error('Failed to fetch sources');
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
      toast.error('Fehler beim Laden der Quellen');
      return [];
    }
  }, []);
  
  // P1.4.1: Fetch health for a single source
  const fetchSourceHealth = useCallback(async (sourceId) => {
    setLoadingHealth(prev => ({ ...prev, [sourceId]: true }));
    try {
      const response = await fetch(`${API}/api/source-registry/${sourceId}/health`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setHealthData(prev => ({ ...prev, [sourceId]: data }));
      }
    } catch (error) {
      console.error(`Error fetching health for ${sourceId}:`, error);
    } finally {
      setLoadingHealth(prev => ({ ...prev, [sourceId]: false }));
    }
  }, []);
  
  // P1.4.1: Fetch health for all sources
  const fetchAllHealth = useCallback(async (sourcesList) => {
    const sources = sourcesList || [];
    // Fetch health in parallel (batch of 5)
    for (let i = 0; i < sources.length; i += 5) {
      const batch = sources.slice(i, i + 5);
      await Promise.all(batch.map(s => fetchSourceHealth(s.source_id)));
    }
  }, [fetchSourceHealth]);
  
  // P1.4.1: Improved refresh handler with loading state
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [_, sourcesList] = await Promise.all([fetchStats(), fetchSources()]);
      await fetchAllHealth(sourcesList);
      toast.success('Daten aktualisiert');
    } catch (error) {
      toast.error('Aktualisierung fehlgeschlagen');
    } finally {
      setRefreshing(false);
    }
  };

  // Lazy load data only when tab is active
  useEffect(() => {
    const loadData = async () => {
      if (!isActive || initialLoadDone) return;
      setLoading(true);
      try {
        const [_, sourcesList] = await Promise.all([fetchStats(), fetchSources()]);
        await fetchAllHealth(sourcesList);
      } catch (error) {
        console.error('Initial load error:', error);
      } finally {
        setLoading(false);
        setInitialLoadDone(true);
      }
    };
    loadData();
  }, [isActive, initialLoadDone, fetchStats, fetchSources, fetchAllHealth]);
  
  // Filter sources based on toggle
  const filteredSources = showOnlyEffective 
    ? sources.filter(s => s.status === 'ACTIVE' && s.tier_approved_by)
    : sources;

  // Migrate KB documents
  const handleMigrate = async () => {
    try {
      toast.info('Migriere bestehende Dokumente...');
      const response = await fetch(`${API}/api/source-registry/migrate-kb-documents`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      toast.success(`Migration: ${data.migrated} migriert, ${data.skipped} übersprungen`);
      await Promise.all([fetchStats(), fetchSources()]);
    } catch (error) {
      toast.error('Migration fehlgeschlagen: ' + error.message);
    }
  };

  // Approve tier
  const handleApproveTier = async (sourceId, tier, rank) => {
    try {
      const response = await fetch(`${API}/api/source-registry/${sourceId}/approve-tier`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ tier, precedence_rank: rank })
      });
      if (response.ok) {
        toast.success(`Tier ${tier} genehmigt (Rang: ${rank})`);
        await Promise.all([fetchStats(), fetchSources()]);
        setTierModal({ open: false, source: null });
      } else {
        const error = await response.json();
        toast.error('Fehler: ' + error.detail);
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Reprocess Evidence - regenerate chunks for a source
  const handleReprocessEvidence = async (sourceId) => {
    if (!window.confirm('Evidence neu verarbeiten und Chunks regenerieren?')) return;
    
    toast.info('Verarbeite Evidence neu...');
    try {
      const response = await fetch(`${API}/api/source-registry/${sourceId}/reprocess-evidence`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.created_chunks > 0) {
          toast.success(`${data.created_chunks} Chunks erstellt!`);
        } else {
          toast.warning('Keine Chunks erstellt - prüfen Sie die Evidence');
        }
        // Refresh health data for this source
        await fetchSourceHealth(sourceId);
        await fetchSources();
      } else {
        toast.error('Fehler: ' + data.detail);
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Deprecate source
  const handleDeprecate = async (sourceId) => {
    if (!window.confirm('Quelle wirklich als veraltet markieren?')) return;
    
    try {
      const response = await fetch(`${API}/api/source-registry/${sourceId}/deprecate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Quelle als veraltet markiert');
        await Promise.all([fetchStats(), fetchSources()]);
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  // Handle reset completion
  const handleResetComplete = async (data) => {
    await Promise.all([fetchStats(), fetchSources()]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2">Lade Source Registry...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-blue-600" />
            Source Registry
          </h2>
          <p className="text-gray-600 mt-1">
            Unified Governance für alle Evidence-Quellen
          </p>
        </div>
        <div className="flex gap-2">
          {/* PRIMARY CTA - Wizard */}
          <Button
            onClick={() => setShowWizard(true)}
            data-testid="add-source-evidence-btn"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Source & Evidence hinzufügen
          </Button>
          
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-sources-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Lädt...' : 'Aktualisieren'}
          </Button>
          
          {/* Admin Tools Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="admin-tools-dropdown">
                <Wrench className="w-4 h-4 mr-2" />
                Admin Tools
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={handleMigrate}>
                <Database className="w-4 h-4 mr-2" />
                KB Docs migrieren
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => setShowResetDialog(true)}
                className="text-orange-600 focus:text-orange-600"
                data-testid="scoped-reset-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                AI Engine Reset (sicher)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Scoped Reset Dialog */}
      <ScopedResetDialog
        isOpen={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onResetComplete={handleResetComplete}
      />

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total_sources}</div>
              <div className="text-sm text-gray-600">Gesamt Quellen</div>
            </CardContent>
          </Card>
          
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-700">
                {stats.pending_review}
              </div>
              <div className="text-sm text-yellow-600 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Ausstehende Reviews
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-2">
                <Badge className={TIER_COLORS.PRIMARY}>
                  {stats.by_tier?.PRIMARY || 0} PRIMARY
                </Badge>
                <Badge className={TIER_COLORS.SECONDARY}>
                  {stats.by_tier?.SECONDARY || 0} SEC
                </Badge>
              </div>
              <div className="text-sm text-gray-600 mt-2">Nach Tier</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex gap-2">
                <Badge className={STATUS_COLORS.ACTIVE}>
                  {stats.by_status?.ACTIVE || 0} Aktiv
                </Badge>
                <Badge className={STATUS_COLORS.DEPRECATED}>
                  {stats.by_status?.DEPRECATED || 0} Veraltet
                </Badge>
              </div>
              <div className="text-sm text-gray-600 mt-2">Nach Status</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Precedence Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-800">Source Precedence</h4>
              <p className="text-sm text-blue-700 mt-1">
                <strong>PRIMARY</strong> schlägt <strong>SECONDARY</strong> schlägt <strong>TERTIARY</strong>.
                Bei Konflikten gewinnt immer die Quelle mit höherem Tier und höherem Precedence-Rang.
                Neue Quellen starten als TERTIARY + PENDING_REVIEW.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sources List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Registrierte Quellen ({filteredSources.length}{showOnlyEffective ? ` von ${sources.length}` : ''})
            </CardTitle>
            
            {/* P1.4.1: Filter Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                id="show-effective"
                checked={showOnlyEffective}
                onCheckedChange={setShowOnlyEffective}
              />
              <Label htmlFor="show-effective" className="text-sm text-gray-600">
                Nur effektive Quellen
              </Label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredSources.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
              {showOnlyEffective ? (
                <>
                  <p>Keine effektiven Quellen gefunden.</p>
                  <p className="text-sm mt-2">
                    Deaktivieren Sie den Filter oder genehmigen Sie Quellen.
                  </p>
                </>
              ) : (
                <>
                  <p>Keine Quellen registriert.</p>
                  <p className="text-sm mt-2">
                    Klicken Sie auf "Source & Evidence hinzufügen" um zu beginnen.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSources.map((source) => {
                const TypeIcon = TYPE_ICONS[source.source_type] || FileText;
                const isExpanded = expandedSource === source.source_id;
                const health = healthData[source.source_id];
                const HealthIcon = health ? HEALTH_ICONS[health.status] : null;
                
                return (
                  <div 
                    key={source.source_id}
                    className="border rounded-lg p-4 hover:bg-gray-50"
                  >
                    {/* Main Row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <TypeIcon className="w-5 h-5 text-gray-500" />
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {source.title}
                            {/* P1.4.1: Health Badge */}
                            {health && (
                              <Badge 
                                className={`text-xs ${HEALTH_COLORS[health.status]}`}
                                title={health.admin_hint}
                              >
                                {HealthIcon && <HealthIcon className="w-3 h-3 mr-1" />}
                                {health.status === 'OK' ? 'OK' : health.status.replace('_', ' ')}
                              </Badge>
                            )}
                            {loadingHealth[source.source_id] && (
                              <RefreshCw className="w-3 h-3 animate-spin text-gray-400" />
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {source.source_id} | {source.source_type}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* P1.4.1: Clickable Evidence Count */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onNavigateToEvidence) {
                              onNavigateToEvidence(source.source_id);
                            } else {
                              toast.info(`Evidence für ${source.title}: ${health?.evidence_count || 0} Dokumente`);
                            }
                          }}
                          className="text-xs px-2 py-1 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 flex items-center gap-1 transition-colors"
                          title="Klicken für Evidence Drilldown"
                          data-testid={`evidence-count-${source.source_id}`}
                        >
                          <Eye className="w-3 h-3" />
                          {health?.evidence_count ?? '?'} Evidence
                        </button>
                        
                        {/* P1.4.1: Clickable Chunk Count */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onNavigateToChunks) {
                              onNavigateToChunks(source.source_id);
                            } else {
                              toast.info(`Chunks für ${source.title}: ${health?.chunk_count || 0} Chunks`);
                            }
                          }}
                          className="text-xs px-2 py-1 rounded bg-purple-50 hover:bg-purple-100 text-purple-700 flex items-center gap-1 transition-colors"
                          title="Klicken für Chunks Drilldown"
                          data-testid={`chunk-count-${source.source_id}`}
                        >
                          <Layers className="w-3 h-3" />
                          {health?.chunk_count ?? '?'} Chunks
                        </button>
                        
                        <Badge className={TIER_COLORS[source.tier]}>
                          {source.tier}
                        </Badge>
                        <Badge className={STATUS_COLORS[source.status]}>
                          {source.status}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          Rang: {source.precedence_rank}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedSource(isExpanded ? null : source.source_id)}
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Exams:</span>
                            <div>{source.exam_ids?.join(', ') || '-'}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Version:</span>
                            <div>{source.version || source.edition || '-'}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Publisher:</span>
                            <div>{source.publisher || '-'}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Sprache:</span>
                            <div>{source.language}</div>
                          </div>
                        </div>
                        
                        {source.tier_approved_by && (
                          <div className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Genehmigt von {source.tier_approved_by}
                          </div>
                        )}
                        
                        {source.status === 'PENDING_REVIEW' && (
                          <div className="flex items-center gap-2 text-sm text-yellow-600">
                            <AlertTriangle className="w-4 h-4" />
                            Tier-Genehmigung ausstehend
                          </div>
                        )}
                        
                        {/* P1.4.1: Health Details */}
                        {health && (
                          <div className={`p-3 rounded-lg ${
                            health.status === 'OK' ? 'bg-green-50' :
                            health.status === 'NO_EVIDENCE' ? 'bg-red-50' : 'bg-yellow-50'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {HealthIcon && <HealthIcon className={`w-5 h-5 ${
                                  health.status === 'OK' ? 'text-green-600' :
                                  health.status === 'NO_EVIDENCE' ? 'text-red-600' : 'text-yellow-600'
                                }`} />}
                                <span className="font-medium">
                                  {health.status === 'OK' ? 'Vollständig' : 
                                   health.status === 'NO_EVIDENCE' ? 'Keine Evidence' : 
                                   health.status === 'CHUNKING_FAILED' ? 'Chunking fehlgeschlagen' : 'Keine Chunks'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Reprocess Evidence Button for NO_CHUNKS or CHUNKING_FAILED */}
                                {(health.status === 'NO_CHUNKS' || health.status === 'CHUNKING_FAILED') && health.evidence_count > 0 && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                    onClick={() => handleReprocessEvidence(source.source_id)}
                                    data-testid={`reprocess-${source.source_id}`}
                                  >
                                    <Wrench className="w-4 h-4 mr-1" />
                                    Chunks regenerieren
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => fetchSourceHealth(source.source_id)}
                                  disabled={loadingHealth[source.source_id]}
                                >
                                  <RefreshCw className={`w-4 h-4 ${loadingHealth[source.source_id] ? 'animate-spin' : ''}`} />
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{health.admin_hint}</p>
                            {/* Chunking diagnostics */}
                            {health.last_chunking_status && health.last_chunking_status !== 'NOT_RUN' && (
                              <div className="mt-2 text-xs text-gray-500 space-y-1">
                                <div>Chunking Status: <span className={
                                  health.last_chunking_status === 'SUCCESS' ? 'text-green-600' :
                                  health.last_chunking_status === 'FAILED' ? 'text-red-600' : 'text-yellow-600'
                                }>{health.last_chunking_status}</span></div>
                                {health.last_chunking_run_at && (
                                  <div>Letzter Lauf: {new Date(health.last_chunking_run_at).toLocaleString()}</div>
                                )}
                                {health.last_chunking_error && (
                                  <div className="text-red-600">Fehler: {health.last_chunking_error}</div>
                                )}
                              </div>
                            )}
                            {health.last_ingestion_at && (
                              <p className="text-xs text-gray-500 mt-1">
                                Letzte Ingestion: {new Date(health.last_ingestion_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            onClick={() => setTierModal({ open: true, source })}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Tier ändern
                          </Button>
                          {source.status !== 'DEPRECATED' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300"
                              onClick={() => handleDeprecate(source.source_id)}
                            >
                              Als veraltet markieren
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tier Approval Modal */}
      {tierModal.open && tierModal.source && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Tier genehmigen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <strong>Quelle:</strong> {tierModal.source.title}
              </div>
              <div>
                <strong>Aktueller Tier:</strong>{' '}
                <Badge className={TIER_COLORS[tierModal.source.tier]}>
                  {tierModal.source.tier}
                </Badge>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium">Neuer Tier:</label>
                <div className="flex gap-2">
                  {['PRIMARY', 'SECONDARY', 'TERTIARY'].map((tier) => (
                    <Button
                      key={tier}
                      variant={tierModal.selectedTier === tier ? 'default' : 'outline'}
                      className={tierModal.selectedTier === tier ? '' : TIER_COLORS[tier]}
                      onClick={() => setTierModal({ ...tierModal, selectedTier: tier })}
                    >
                      {tier}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium">Precedence Rang (höher = stärker):</label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={tierModal.rank || 100}
                  onChange={(e) => setTierModal({ ...tierModal, rank: parseInt(e.target.value) })}
                />
              </div>
              
              <div className="flex gap-2 justify-end pt-4">
                <Button
                  variant="outline"
                  onClick={() => setTierModal({ open: false, source: null })}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={() => handleApproveTier(
                    tierModal.source.source_id,
                    tierModal.selectedTier || tierModal.source.tier,
                    tierModal.rank || 100
                  )}
                  disabled={!tierModal.selectedTier}
                >
                  Genehmigen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Source & Evidence Wizard */}
      <SourceEvidenceWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onSuccess={() => {
          Promise.all([fetchStats(), fetchSources()]);
        }}
      />
    </div>
  );
};

export default SourceRegistryManager;
